import { useFormContext } from 'react-hook-form';
import { useMutation } from 'react-query';

import { NOTIFICATION_TYPES } from '@savgroup-front-common/constants';
import { ADDITIONAL_INFORMATION_TYPES } from '@savgroup-front-common/types';

import { CommonCatalogService } from '../../../../api/Catalog';
import { safeFormattedIntlString } from '../../../../formatters';
import { base64FromArrayBuffer, buildNotification } from '../../../../helpers';
import { useToasts } from '../../../../molecules/NotificationsProvider';
import { useAdditionalInformationContext } from '../../AdditionalInformation.context';
import { buildInputName } from '../../AdditionalInformationInput.helpers';

import messages from './messages';

export const useProcessNamePlateForSerialNumber = ({
  claimIds,
}: {
  claimIds: string[];
}) => {
  const { pushNotification } = useToasts();
  const { getValues, setValue } = useFormContext();
  const { pushError } = useToasts();
  const {
    neededInformation: neededInformationItems,
    prefixFormName,
    onAdditionalInformationSave,
  } = useAdditionalInformationContext();

  const {
    mutateAsync: processNamePlateForSerialNumber,
    isLoading: isProcessingNamePlate,
  } = useMutation<void, unknown, Blob | File>(
    ['processNamePlateForSerialNumber'],
    async (blob: Blob | File) => {
      try {
        const base64 = await base64FromArrayBuffer(blob);

        const response =
          await CommonCatalogService.recognizeSerialNumberCommand({
            imageBase64: base64,
          });

        if (response.failure) {
          pushNotification(
            buildNotification({
              notificationType: NOTIFICATION_TYPES.INFO,
              message: safeFormattedIntlString(
                messages.unrecognizedSerialNumber,
              ),
            }),
          );

          return;
        }

        const serialNumber = response.value;

        if (serialNumber) {
          claimIds?.map((claimId) => {
            neededInformationItems.forEach((neededInformationItem) => {
              if (
                claimId &&
                neededInformationItem.type ===
                  ADDITIONAL_INFORMATION_TYPES.SERIAL_NUMBER &&
                neededInformationItem.claimIds?.includes(claimId) &&
                neededInformationItem.claimIds.length > 0
              ) {
                const targetName = buildInputName({
                  prefixFormName,
                  name: neededInformationItem.internalId,
                });

                const targetValue = getValues(targetName);

                if (!targetValue) {
                  setValue(targetName, serialNumber, {
                    shouldValidate: true,
                    shouldDirty: true,
                  });
                }
              }
            });
          });

          if (onAdditionalInformationSave) {
            await onAdditionalInformationSave();
          }

          return;
        }

        pushNotification(
          buildNotification({
            notificationType: NOTIFICATION_TYPES.INFO,
            message: safeFormattedIntlString(messages.unrecognizedSerialNumber),
          }),
        );
      } catch (error) {
        pushError(
          new Error(safeFormattedIntlString(messages.errorProcessingFile)),
        );
      }
    },
  );

  return {
    processNamePlateForSerialNumber,
    isProcessingNamePlate,
  };
};
